import { modelBadge, shortLabel, tierCssClass, fmtTokens } from '../utils/modelHelpers';
import { CASCADE_MODEL_ID } from '@voice-assistant/shared';
import type { RegistryModel } from '../hooks/useModels';

/**
 * Reusable provider-grouped model list (extracted from ModelControlSheet, P2.1 of the
 * Harness Studio plan). Renders each provider's models as selectable rows with badge +
 * description + capability chips, and calls `onPick(modelId)` on tap. Pure + self-contained
 * (takes `models`, computes its own groups) so the Harness Studio editor (Phase 2.3/2.4) can
 * reuse it to pick a model for a cascade binding.
 *
 * NOTE: keeps the existing `mcs-*` CSS classes verbatim — the styling is global, so this is a
 * byte-identical render of ModelControlSheet's former inline "switch model" list (ZERO
 * behaviour change). A future cosmetic pass can rename to `mpl-*` if desired.
 */

/* ── Helpers (moved here from ModelControlSheet; exported for the sheet's consult grid) ── */

export function modelCapabilityChips(m: RegistryModel): string[] {
  const caps = m.capabilities;
  const chips: string[] = [];
  if (caps?.supportedEffortLevels?.includes('max')) chips.push('Extended thinking');
  else if (caps?.supportsEffort) chips.push('Thinking');
  if (caps?.supports1MContext) chips.push('1M context');
  if (m.modelId.includes('2.5-pro') || m.modelId.includes('2.0-pro')) chips.push('Multimodal');
  if (m.provider === 'ollama') chips.push('Local · Private');
  if (m.provider === 'qwen') chips.push('Local · $0', 'Tools');
  if (m.provider === 'local-5090') chips.push('Local · $0'); // plain chat — no tools
  if (m.modelId.includes('haiku')) chips.push('Fastest');
  if (m.modelId.includes('2.5-flash') || m.modelId.includes('2.0-flash')) chips.push('Fast');
  return chips;
}

export function modelDescription(m: RegistryModel): string {
  const id = m.modelId.toLowerCase();
  if (id.includes('opus'))      return 'Deep reasoning · Complex tasks · Architecture';
  if (id.includes('sonnet'))    return 'Balanced performance · Everyday development';
  if (id.includes('haiku'))     return 'Fast & lightweight · Quick questions';
  if (id.includes('2.5-pro'))   return 'Long context · Multimodal · Google knowledge';
  if (id.includes('2.5-flash')) return 'Smart & fast · Google balanced';
  if (id.includes('2.0-flash')) return 'High speed · Budget-friendly';
  if (m.provider === 'local-5090') return 'Local chat · Zero API cost · runs on your GPU';
  if (m.provider === 'qwen')    return 'Local coding agent · Tools · Zero API cost';
  if (m.provider === 'google')  return 'Google AI model';
  if (m.provider === 'ollama')  return 'Local · Private · Zero API cost';
  return m.displayName ?? m.modelId;
}

export const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  google: 'Google',
  ollama: 'Local (Ollama)',
  qwen: 'Qwen (local)',
  'local-5090': 'Gemma (local)',
  kimi: 'Kimi (Moonshot)',
};

export const PROVIDER_COLORS: Record<string, string> = {
  anthropic: '#d4845a',
  google: '#34a853',
  ollama: '#a0b4c8',
  qwen: '#7a5cff',
  'local-5090': '#5cb88a',
  kimi: '#ff8f4d',
};

export function toRgba(hex: string, alpha: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return `rgba(128,128,128,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function groupByProvider(models: RegistryModel[]) {
  // 'local-5090' (Gemma local chat) + 'kimi' (Kimi K3, Moonshot) sit at the end of the curated
  // order — both are admin/gate-surfaced additions on top of the original 4-provider order.
  // Providers NOT in `order` are NO LONGER dropped (model-picker-provider-parity, #1973): the
  // pre-parity silent-drop made provider N+1 invisible in every picker built on this helper until
  // a human edited the array. Unknown providers append after the curated ones, in first-appearance
  // (registry) order, with a graceful fallback label (the raw key) and the '#888' fallback colour
  // at the render sites. Adding a curated label/colour for a new provider stays a one-map edit in
  // PROVIDER_LABELS/PROVIDER_COLORS above. See useModels.ts RegistryModel.provider.
  const order = ['anthropic', 'google', 'qwen', 'ollama', 'local-5090', 'kimi'];
  const map = new Map<string, RegistryModel[]>();
  for (const m of models) {
    const list = map.get(m.provider) ?? [];
    list.push(m);
    map.set(m.provider, list);
  }
  const toGroup = (p: string) => ({ provider: p, label: PROVIDER_LABELS[p] ?? p, models: map.get(p)! });
  const known = order.filter((p) => map.has(p)).map(toGroup);
  const unknown = [...map.keys()].filter((p) => !order.includes(p)).map(toGroup);
  return [...known, ...unknown];
}

/* ── Mobile FAB long-press census (model-picker-provider-parity, #1973) ── */

/**
 * One item of the mobile FAB long-press picker's compact rows — the same census as the desktop
 * caret picker, in the mobile door's compact presentation (short label + version tag + one
 * context/cost chip). All fields derive from the registry row's capabilities, never from a
 * provider-key allow-list.
 */
export interface FabModelItem {
  id: string;
  label: string;
  tag: string;
  cls: string;
  /** ctx chip text: '$0' for local models (capabilities.local), else the human context window
   *  (e.g. '200K', '1M'), else null (chip hidden). */
  ctx: string | null;
  /** native-1M styling on the ctx chip (capabilities.supports1MContext) — the mobile door's
   *  equivalent of the desktop '1M context' capability chip. */
  native1m: boolean;
  /** Tooltip for the ctx chip. */
  ctxTitle: string | null;
}

export interface FabModelGroup {
  provider: string;
  label: string;
  /** Resolved section colour: PROVIDER_COLORS[provider] with the ModelPickerList '#888' fallback. */
  color: string;
  items: FabModelItem[];
}

/**
 * The mobile FAB long-press census: every registry model, grouped by the SHARED groupByProvider
 * (the desktop caret picker's deriver), minus Cascade — the plain FAB tap is the only door to a
 * Cascade session ("two doors → confusion"), a deliberate product choice preserved from the
 * pre-parity picker. Provider N+1 appears here with NO frontend edit (groupByProvider surfaces
 * unknown providers; unknown → graceful fallback label/colour).
 */
export function fabModelGroups(models: RegistryModel[]): FabModelGroup[] {
  const fixed = models.filter((m) => m.modelId !== CASCADE_MODEL_ID);
  return groupByProvider(fixed).map((g) => ({
    provider: g.provider,
    label: g.label,
    color: PROVIDER_COLORS[g.provider] ?? '#888',
    items: g.models.map((m): FabModelItem => {
      const sl = shortLabel(m);
      const caps = m.capabilities;
      const local = !!caps?.local;
      const ctx = local ? '$0' : fmtTokens(m.maxInputTokens);
      return {
        id: m.modelId,
        label: sl.label,
        tag: sl.tag ?? '',
        cls: tierCssClass(m),
        ctx,
        native1m: !local && !!caps?.supports1MContext,
        ctxTitle: local
          ? caps?.toolCapable
            ? 'Local · $0 · tool-using coding agent'
            : 'Local · $0 · runs on your GPU'
          : ctx
            ? `${ctx} context window`
            : null,
      };
    }),
  }));
}

/* ── Component ─────────────────────────────────────────────────────── */

interface ModelPickerListProps {
  models: RegistryModel[];
  /** Highlighted (✓) model id. When null, the registry default (`m.isDefault`) is highlighted. */
  selectedModelId: string | null;
  /** Fired when a model row is tapped. */
  onPick: (modelId: string) => void;
  /** Hide the Google provider group on gemini-disabled deployments (protofac/valko). Default true. */
  geminiEnabled?: boolean;
  /**
   * OPT-IN menu semantics (W2 / MP-1, issue #1966). Omitted (the default) → the historical
   * render: plain `<label>` rows with no ARIA role, correct inside a sheet or a page section.
   * Set to `'menuitemradio'` by a caller that wraps this list in a `role="menu"` container
   * (NewSessionModelPicker's popover) — the rows then become real menu items with
   * `aria-checked`, a roving `tabindex`, and a `data-model-id` handle, and the provider blocks
   * become `role="group"` so the menu's only children are groups and items.
   *
   * WHY OPT-IN AND NOT ALWAYS-ON: `role="menuitemradio"` is a LIE outside a menu — it tells a
   * screen reader "you are in a menu; arrows move you" when nothing implements arrows. Three of
   * this component's four consumers (ModelControlSheet, HarnessPage, and the mobile FAB census)
   * are not menus. The keyboard behaviour that makes the role true lives with the container that
   * owns it, so the role has to be requested by that container — never assumed here.
   */
  itemRole?: 'menuitemradio';
}

export function ModelPickerList({ models, selectedModelId, onPick, geminiEnabled = true, itemRole }: ModelPickerListProps) {
  const groups = groupByProvider(models).filter((g) => geminiEnabled || g.provider !== 'google');
  const asMenu = itemRole === 'menuitemradio';

  // Roving tabindex (menu mode only): the WHOLE list is ONE Tab stop, arrows move within it —
  // the same contract SE-DS-10 already pins for the sidebar session list. The stop is the
  // checked row when there is one, else the first row in flattened group order. Computed here
  // rather than in the container because only this component knows the flattened order.
  const flat = groups.flatMap((g) => g.models);
  const rovingId = asMenu
    ? (flat.find((m) => m.modelId === selectedModelId) ?? flat[0])?.modelId ?? null
    : null;

  return (
    <>
      {groups.map((group, gi) => (
        <div
          key={group.provider}
          class={`mcs-provider-group${gi > 0 ? ' mcs-provider-group--gap' : ''}`}
          role={asMenu ? 'group' : undefined}
          aria-label={asMenu ? group.label : undefined}
        >
          <div class="mcs-group-header" style={`--pc: ${PROVIDER_COLORS[group.provider] ?? '#888'}`}>
            <span class="mcs-group-dot" />
            <span class="mcs-group-name">{group.label}</span>
          </div>
          {group.models.map((m) => {
            const isSelected = selectedModelId
              ? m.modelId === selectedModelId || selectedModelId.includes(m.modelId) || m.modelId.includes(selectedModelId)
              : m.isDefault;
            const badge = modelBadge(m.modelId, false);
            const mc = badge.color;
            return (
              <div key={m.modelId} class="mcs-model-cell">
                {/* Still a <label>, deliberately: `.mcs-model-row` is GLOBAL css authored against
                    a label, and swapping the tag would silently restyle the mobile sheet and the
                    Harness Studio editor. The role/tabindex/data handle below are additive — in
                    menu mode the container drives activation, so the tag carries no behaviour. */}
                <label
                  class={`mcs-model-row${isSelected ? ' selected' : ''}`}
                  style={`--mc: ${mc}; --mc-bg: ${toRgba(mc, isSelected ? 0.10 : 0)}; --mc-border: ${toRgba(mc, isSelected ? 0.55 : 0)}`}
                  onClick={() => onPick(m.modelId)}
                  role={itemRole}
                  aria-checked={asMenu ? isSelected : undefined}
                  tabIndex={asMenu ? (m.modelId === rovingId ? 0 : -1) : undefined}
                  data-model-id={asMenu ? m.modelId : undefined}
                >
                  <div
                    class="mcs-avatar"
                    style={`background: ${toRgba(mc, 0.18)}; border-color: ${toRgba(mc, 0.35)}; color: ${mc}`}
                  >
                    {badge.label}
                  </div>
                  <div class="mcs-model-info">
                    <span class="mcs-model-name">{m.displayName ?? m.modelId}</span>
                    <span class="mcs-model-desc">{modelDescription(m)}</span>
                    {modelCapabilityChips(m).length > 0 && (
                      <div class="mcs-cap-chips">
                        {modelCapabilityChips(m).map(chip => (
                          <span key={chip} class="mcs-cap-chip">{chip}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <span class="mcs-checkmark" style={`color: ${mc}`}>✓</span>
                  )}
                </label>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}
